import { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  FileText, FloppyDisk, DownloadSimple, Plus, Trash,
} from "@phosphor-icons/react";

const PROJECT_TYPES = [
  { key: "residential", label: "Residential" },
  { key: "commercial",  label: "Commercial" },
  { key: "interior",    label: "Interior" },
  { key: "turnkey",     label: "Turnkey Civil" },
];

const DEFAULT_MILESTONES = [
  { label: "Booking & Advance Token (Upon signing this Agreement)", percentage: 20, cumulative: 20 },
  { label: "Concept Plan Approval (Finalization of 2D floor plans)", percentage: 20, cumulative: 40 },
  { label: "3D Elevation & Material Finalization", percentage: 20, cumulative: 60 },
  { label: "Issue of Structural & Municipal/Working Drawings", percentage: 25, cumulative: 85 },
  { label: "During Construction & Slab Castings (Pro-rata across site visits)", percentage: 10, cumulative: 95 },
  { label: "Final Handover / Completion Drawings", percentage: 5, cumulative: 100 },
];

// Map project.category (civil | interior) to preselected agreement types.
function categoryToTypes(category) {
  if (category === "interior") return ["interior"];
  if (category === "civil") return ["residential"];
  return [];
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * AgreementDialog — form to build/edit the architectural design & consultancy
 * agreement for a project. Prefills every field it can from the project doc,
 * lets the studio override anything, save to Mongo, and download the PDF.
 */
export default function AgreementDialog({ project, open, onOpenChange }) {
  const projectName = project?.name || "";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [form, setForm] = useState({
    effective_date: todayISO(),
    project_types: [],
    site_address: "",
    plot_area_sqft: "",
    client_name: "",
    client_address: "",
    client_contact: "",
    client_email: "",
    total_fee: "",
    total_fee_words: "",
    fee_per_sqft: "",
    site_visits_count: "",
    revision_rate: "",
    milestones: DEFAULT_MILESTONES,
  });

  // Seed form from project + fetch existing agreement whenever the dialog opens.
  const prefill = useCallback(async () => {
    if (!project || !open) return;
    setLoading(true);
    const prefilled = {
      effective_date: todayISO(),
      project_types: categoryToTypes(project.category),
      site_address: project.client_address || "",
      plot_area_sqft: project.area_sqft ? String(project.area_sqft) : "",
      client_name: project.client_name || "",
      client_address: project.client_address || "",
      client_contact: project.client_contact || "",
      client_email: project.client_email || "",
      total_fee: project.total_fee ?? "",
      total_fee_words: "",
      fee_per_sqft: project.rate_per_sqft ?? "",
      site_visits_count: "",
      revision_rate: "",
      milestones: DEFAULT_MILESTONES,
    };
    try {
      const { data } = await http.get(
        `/projects/${encodeURIComponent(project.name)}/agreement`
      );
      if (data) {
        setForm({
          ...prefilled,
          ...Object.fromEntries(
            Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== "")
          ),
          milestones: (data.milestones && data.milestones.length) ? data.milestones : DEFAULT_MILESTONES,
          project_types: data.project_types?.length ? data.project_types : prefilled.project_types,
        });
      } else {
        setForm(prefilled);
      }
    } catch (e) {
      setForm(prefilled);
      if (e?.response?.status && e.response.status !== 404) {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  }, [project, open]);

  useEffect(() => { prefill(); }, [prefill]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (key) => {
    setForm((f) => {
      const has = f.project_types.includes(key);
      return {
        ...f,
        project_types: has
          ? f.project_types.filter((t) => t !== key)
          : [...f.project_types, key],
      };
    });
  };

  const updateMilestone = (idx, key, value) => {
    setForm((f) => {
      const next = f.milestones.map((m, i) => (i === idx ? { ...m, [key]: value } : m));
      return { ...f, milestones: next };
    });
  };

  const addMilestone = () => {
    setForm((f) => ({
      ...f,
      milestones: [...f.milestones, { label: "", percentage: 0, cumulative: 0 }],
    }));
  };

  const removeMilestone = (idx) => {
    setForm((f) => ({
      ...f,
      milestones: f.milestones.filter((_, i) => i !== idx),
    }));
  };

  const buildPayload = () => ({
    effective_date: form.effective_date || null,
    project_types: form.project_types,
    site_address: form.site_address,
    plot_area_sqft: form.plot_area_sqft ? String(form.plot_area_sqft) : "",
    client_name: form.client_name,
    client_address: form.client_address,
    client_contact: form.client_contact,
    client_email: form.client_email,
    total_fee: form.total_fee === "" ? null : Number(form.total_fee),
    total_fee_words: form.total_fee_words,
    fee_per_sqft: form.fee_per_sqft === "" ? null : Number(form.fee_per_sqft),
    site_visits_count: form.site_visits_count === "" ? null : Number(form.site_visits_count),
    revision_rate: form.revision_rate === "" ? null : Number(form.revision_rate),
    milestones: form.milestones.map((m) => ({
      label: m.label || "",
      percentage: Number(m.percentage) || 0,
      cumulative: Number(m.cumulative) || 0,
    })),
  });

  const save = async () => {
    setSaving(true);
    try {
      await http.put(
        `/projects/${encodeURIComponent(projectName)}/agreement`,
        buildPayload()
      );
      toast.success("Agreement saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      // Save first so the PDF reflects the current form state.
      await http.put(
        `/projects/${encodeURIComponent(projectName)}/agreement`,
        buildPayload()
      );
      const { data } = await http.get(
        `/projects/${encodeURIComponent(projectName)}/agreement/pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Agreement-${projectName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Agreement downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="agreement-dialog"
        className="rounded-none border-2 border-zinc-900 shadow-brutal max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="label-overline text-orange-700 mb-1 flex items-center gap-1.5">
            <FileText size={12} weight="bold" /> Architectural Agreement
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tighter uppercase">
            {projectName || "New Agreement"}
          </DialogTitle>
          <div className="label-overline text-zinc-500 mt-1">
            Prefilled from the project · edit anything before download
          </div>
        </DialogHeader>

        {loading && (
          <div className="label-overline text-zinc-500 py-6 text-center">Loading agreement…</div>
        )}

        <div className="space-y-5 mt-3">
          {/* Effective date + project types */}
          <section>
            <div className="label-overline text-zinc-700 mb-2">Effective Date & Project Type</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <label className="label-overline text-zinc-500">Effective Date</label>
                <Input
                  type="date"
                  data-testid="agreement-effective-date"
                  value={form.effective_date || ""}
                  onChange={(e) => setField("effective_date", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline text-zinc-500">Project Type (tick all that apply)</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PROJECT_TYPES.map((t) => {
                    const active = form.project_types.includes(t.key);
                    return (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`agreement-type-${t.key}`}
                        onClick={() => toggleType(t.key)}
                        className={`h-9 px-3 border-2 text-xs font-mono uppercase tracking-widest transition-colors ${
                          active
                            ? "bg-orange-600 border-orange-700 text-white"
                            : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-500"
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Site */}
          <section>
            <div className="label-overline text-zinc-700 mb-2">Site Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="label-overline text-zinc-500">Site Address / Plot No.</label>
                <Input
                  data-testid="agreement-site-address"
                  value={form.site_address}
                  onChange={(e) => setField("site_address", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div>
                <label className="label-overline text-zinc-500">Plot / Built-up Area (sq. ft.)</label>
                <Input
                  data-testid="agreement-plot-area"
                  value={form.plot_area_sqft}
                  onChange={(e) => setField("plot_area_sqft", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
            </div>
          </section>

          {/* Client */}
          <section>
            <div className="label-overline text-zinc-700 mb-2">Client Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline text-zinc-500">Client Name</label>
                <Input
                  data-testid="agreement-client-name"
                  value={form.client_name}
                  onChange={(e) => setField("client_name", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div>
                <label className="label-overline text-zinc-500">Contact No.</label>
                <Input
                  data-testid="agreement-client-contact"
                  value={form.client_contact}
                  onChange={(e) => setField("client_contact", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline text-zinc-500">Address</label>
                <Textarea
                  data-testid="agreement-client-address"
                  value={form.client_address}
                  onChange={(e) => setField("client_address", e.target.value)}
                  className="rounded-none border-zinc-400 text-xs min-h-[60px]"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline text-zinc-500">Email</label>
                <Input
                  data-testid="agreement-client-email"
                  value={form.client_email}
                  onChange={(e) => setField("client_email", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
            </div>
          </section>

          {/* Fee */}
          <section>
            <div className="label-overline text-zinc-700 mb-2">Fee Structure</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label-overline text-zinc-500">Total Fee (₹)</label>
                <Input
                  type="number"
                  data-testid="agreement-total-fee"
                  value={form.total_fee}
                  onChange={(e) => setField("total_fee", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div>
                <label className="label-overline text-zinc-500">Fee per sq. ft. (₹, optional)</label>
                <Input
                  type="number"
                  data-testid="agreement-fee-per-sqft"
                  value={form.fee_per_sqft}
                  onChange={(e) => setField("fee_per_sqft", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div>
                <label className="label-overline text-zinc-500">Revision Rate (₹/hour)</label>
                <Input
                  type="number"
                  data-testid="agreement-revision-rate"
                  value={form.revision_rate}
                  onChange={(e) => setField("revision_rate", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="label-overline text-zinc-500">Total Fee in Words</label>
                <Input
                  data-testid="agreement-fee-words"
                  value={form.total_fee_words}
                  onChange={(e) => setField("total_fee_words", e.target.value)}
                  placeholder="e.g. Two Lakh Fifty Thousand"
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
            </div>
          </section>

          {/* Phase 4 visits */}
          <section>
            <div className="label-overline text-zinc-700 mb-2">Phase 4 · Site Visits</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="label-overline text-zinc-500">No. of Scheduled Site Visits</label>
                <Input
                  type="number"
                  data-testid="agreement-site-visits"
                  value={form.site_visits_count}
                  onChange={(e) => setField("site_visits_count", e.target.value)}
                  className="rounded-none border-zinc-400 h-9 text-xs"
                />
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="label-overline text-zinc-700">Fee Milestones (editable)</div>
              <button
                type="button"
                data-testid="agreement-add-milestone"
                onClick={addMilestone}
                className="label-overline text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
              >
                <Plus size={11} weight="bold" /> Add Row
              </button>
            </div>
            <div className="border border-zinc-300">
              <div className="grid grid-cols-12 gap-2 px-2 py-2 bg-zinc-50 border-b border-zinc-300 label-overline text-zinc-600">
                <div className="col-span-7">Stage / Milestone</div>
                <div className="col-span-2 text-right">%</div>
                <div className="col-span-2 text-right">Cum. %</div>
                <div className="col-span-1"></div>
              </div>
              {form.milestones.map((m, idx) => (
                <div
                  key={idx}
                  data-testid={`agreement-milestone-row-${idx}`}
                  className="grid grid-cols-12 gap-2 px-2 py-2 border-b border-zinc-200 last:border-b-0 items-center"
                >
                  <Input
                    data-testid={`agreement-milestone-label-${idx}`}
                    value={m.label}
                    onChange={(e) => updateMilestone(idx, "label", e.target.value)}
                    className="col-span-7 rounded-none border-zinc-300 h-8 text-xs"
                  />
                  <Input
                    type="number"
                    data-testid={`agreement-milestone-pct-${idx}`}
                    value={m.percentage}
                    onChange={(e) => updateMilestone(idx, "percentage", e.target.value)}
                    className="col-span-2 rounded-none border-zinc-300 h-8 text-xs text-right"
                  />
                  <Input
                    type="number"
                    data-testid={`agreement-milestone-cum-${idx}`}
                    value={m.cumulative}
                    onChange={(e) => updateMilestone(idx, "cumulative", e.target.value)}
                    className="col-span-2 rounded-none border-zinc-300 h-8 text-xs text-right"
                  />
                  <button
                    type="button"
                    data-testid={`agreement-milestone-remove-${idx}`}
                    onClick={() => removeMilestone(idx)}
                    className="col-span-1 text-zinc-400 hover:text-red-600 flex justify-center"
                    aria-label="Remove milestone"
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-4 mt-3 border-t border-zinc-300">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none border-zinc-400 font-mono uppercase tracking-widest text-xs hover:bg-zinc-100"
          >
            Close
          </Button>
          <Button
            type="button"
            data-testid="agreement-save"
            onClick={save}
            disabled={saving || downloading || loading}
            className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs"
          >
            <FloppyDisk size={12} weight="bold" className="mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            data-testid="agreement-download"
            onClick={download}
            disabled={saving || downloading || loading}
            className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs"
          >
            <DownloadSimple size={12} weight="bold" className="mr-1.5" />
            {downloading ? "Preparing…" : "Save & Download PDF"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
